import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Heatmap from 'highcharts/modules/heatmap';
import HighchartsMore from 'highcharts/highcharts-more';
import { normalizePlotConfig } from 'utils/chat';
import loadingGif from 'images/loader2.webp';

Heatmap(Highcharts);
HighchartsMore(Highcharts);

const HighchartsComponentAlt = ({ chartTypeOuter, chartTitle, data, dataToPlot }) => {
    const chartRef = useRef(null);
    const [chartOptions, setChartOptions] = useState(null);

    const { chartHeading, chartType, xAxisKey, yAxisKeys } = normalizePlotConfig(
        dataToPlot,
        Array.isArray(data) ? data : [],
        chartTitle || 'Report',
        chartTypeOuter || 'line'
    );

    useEffect(() => {
        if (!Array.isArray(data) || data.length === 0) {
            setChartOptions(null);
            return;
        }

        // Build x-axis categories
        const xValues = data.map(row => row?.[xAxisKey]);

        // Build numeric series only for y-keys
        const seriesData = [];
        for (const key of (Array.isArray(yAxisKeys) ? yAxisKeys : [])) {
            const series = data.map(row => {
                const val = row?.[key];
                return typeof val === 'number' ? val : null;
            });
            seriesData.push({ name: key, data: series });
        }

        if (seriesData.length === 0) {
            console.error('Series data is empty or undefined.');
            return;
        }

        // Determine if yAxis is an array (multiple axes) or an object (single axis)
        const yAxisConfig = Array.isArray(yAxisKeys) && yAxisKeys.length > 1
            ? yAxisKeys.map(axis => ({
                title: {
                    text: axis || 'Values',
                    style: { color: '#ffffff', fontFamily: 'var(--font-montserrat)' }
                },
                labels: {
                    style: { color: '#ffffff', fontFamily: 'var(--font-montserrat)' },
                    formatter: function () {
                        return formatNumber(this.value);
                    }
                },
                gridLineWidth: 0,
            }))
            : {
                title: {
                    text: (yAxisKeys && yAxisKeys[0]) || 'Values',
                    style: { color: '#ffffff', fontFamily: 'var(--font-montserrat)' }
                },
                labels: {
                    style: { color: '#ffffff', fontFamily: 'var(--font-montserrat)' },
                    formatter: function () {
                        return formatNumber(this.value);
                    }
                },
                gridLineWidth: 0,
            };

        const options = {
            chart: {
                backgroundColor: 'transparent',
                plotBackgroundColor: 'transparent',
                type: chartType === 'pie' ? 'pie' : chartType,
                height: 350,
            },
            title: {
                text: chartHeading,
                style: { color: '#ffffff', fontFamily: 'var(--font-montserrat)' }
            },
            xAxis: {
                categories: xValues,
                title: {
                    text: xAxisKey,
                    style: { color: '#ffffff', fontFamily: 'var(--font-montserrat)' }
                },
                labels: {
                    style: { color: '#ffffff', fontFamily: 'var(--font-montserrat)' }
                },
                gridLineWidth: 0,
            },
            yAxis: yAxisConfig,
            series: seriesData,
            credits: { enabled: false },
            legend: {
                itemStyle: { color: '#fff', fontFamily: 'var(--font-montserrat)' }
            },
            plotOptions: {
                series: {
                    dataLabels: {
                        enabled: true,
                        color: '#fff',
                        style: { fontFamily: 'var(--font-montserrat)' },
                        formatter: function () {
                            return formatNumber(this.y);
                        }
                    },
                    connectNulls: true,
                    marker: { enabled: true }
                }
            },
            tooltip: {
                style: { fontFamily: 'var(--font-montserrat)' },
                formatter: function () {
                    return `<b>${this.series.name}</b><br/>${this.key}: ${formatNumber(this.y)}`;
                }
            }
        };

        setChartOptions(options);
    }, [chartType, data, chartHeading, xAxisKey, JSON.stringify(yAxisKeys)]);

    const formatNumber = (value) => {
        if (typeof value === 'number') {
            if (value >= 1e9) {
                return (value / 1e9).toFixed(1) + 'B';
            } else if (value >= 1e6) {
                return (value / 1e6).toFixed(1) + 'M';
            } else if (value >= 1e3) {
                return (value / 1e3).toFixed(1) + 'k';
            } else {
                return value.toFixed(2);
            }
        }
        return value; // Return as is if not a number
    };


    if (!chartOptions) {
        const isEmpty = !Array.isArray(data) || data.length === 0;
        return (
            <div className="flex flex-col items-center justify-center my-2 gap-2 min-h-[350px]">
                {isEmpty ? (
                    <span>No data for the current selection</span>
                ) : (
                    <>
                        <div>
                            <img src={loadingGif} alt="loading" style={{ height: '24px' }} />
                        </div>
                        <span>Generating Charts</span>
                    </>
                )}
            </div>
        );
    }

    return <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />;
};

export default HighchartsComponentAlt;